import { useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import {
  FEED_BY_PLAYLIST_ID,
  FEED_BY_ID,
  CREATE_FEED,
  UPDATE_FEED,
  UPDATE_FEED_STATUS,
  MAKE_FEED_PUBLIC,
  DELETE_FEED,
  UPDATE_FEED_THUMBNAIL,
  FEEDS_ADMIN_CATEGORIES,
} from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';

export const FEED_STATUS = { UNPUBLISH: 0, PUBLISH: 1 } as const;
const FETCH_ALL = 1000;

export interface FeedData {
  _id: string;
  name: string;
  description?: string;
  playlistId: string;
  playlistOptions?: {
    shuffle?: boolean;
    resumeOnNextPlay?: boolean;
    orientation?: string;
  };
  author?: string;
  duration?: number;
  orientation?: string;
  type?: string;
  thumbnail?: string;
  bucket?: string;
  status?: number;
  isPublic?: boolean;
  categoryIds?: string[];
  tags?: string[];
  featured?: boolean;
  rank?: number;
  accountId?: string;
  createdBy?: string;
  createdAt?: string;
  lastUpdatedDate?: string;
  lastUpdatedBy?: string;
  lastStatusUpdateDate?: string;
  lastStatusUpdateBy?: string;
  lastApprovedDate?: string;
  lastApprovedBy?: string;
}

export interface FeedCategory {
  _id: string;
  name: string;
  sortOrder?: number;
  sortOrderWidget?: number;
  createdAt?: string;
  lastUpdatedDate?: string;
}

export function useFeedEditor() {
  const { activeTeamId } = useTeamStore();

  // Queries
  const [fetchFeed, { data: feedData, loading: feedLoading }] = useLazyQuery<{
    feedByPlaylistId: FeedData | null;
  }>(FEED_BY_PLAYLIST_ID, { fetchPolicy: 'network-only' });

  const [fetchFeedById, { data: feedByIdData, loading: feedByIdLoading }] = useLazyQuery<{
    feedById: FeedData | null;
  }>(FEED_BY_ID, { fetchPolicy: 'network-only' });

  const [fetchCategories, { data: categoriesData }] = useLazyQuery<{
    feedsAdminCategories: {
      page: { edges: Array<{ node: FeedCategory }> };
      pageData: { total: number };
    };
  }>(FEEDS_ADMIN_CATEGORIES, { fetchPolicy: 'cache-and-network' });

  // Mutations
  const [createFeedMutation] = useMutation<{ createFeed: FeedData }>(CREATE_FEED);
  const [updateFeedMutation] = useMutation<{ updateFeed: FeedData }>(UPDATE_FEED);
  const [updateFeedStatusMutation] = useMutation<{ updateFeedStatus: FeedData }>(
    UPDATE_FEED_STATUS,
  );
  const [makeFeedPublicMutation] = useMutation<{ makeFeedPublic: FeedData }>(MAKE_FEED_PUBLIC);
  const [deleteFeedMutation] = useMutation(DELETE_FEED, {
    refetchQueries: ['MyFeeds', 'PublishedFeeds'],
  });
  const [updateFeedThumbnailMutation] = useMutation<{ updateFeedThumbnail: FeedData }>(
    UPDATE_FEED_THUMBNAIL,
    { refetchQueries: ['MyFeeds', 'PublishedFeeds'] },
  );

  const loadFeed = useCallback(
    (playlistId: string) => {
      // Fetch feed and categories in parallel when dialog opens
      fetchCategories({ variables: { first: FETCH_ALL } });
      return fetchFeed({
        variables: { teamId: activeTeamId, playlistId },
      });
    },
    [activeTeamId, fetchFeed, fetchCategories],
  );

  const loadFeedById = useCallback(
    (feedId: string) => {
      fetchCategories({ variables: { first: FETCH_ALL } });
      return fetchFeedById({ variables: { id: feedId } });
    },
    [fetchFeedById, fetchCategories],
  );

  const createFeed = useCallback(
    async (name: string, playlistId: string, author?: string) => {
      const result = await createFeedMutation({
        variables: {
          teamId: activeTeamId,
          input: { name, playlistId, ...(author && { author }) },
        },
      });
      return result.data?.createFeed as FeedData;
    },
    [activeTeamId, createFeedMutation],
  );

  const updateFeed = useCallback(
    async (id: string, input: Record<string, unknown>) => {
      const result = await updateFeedMutation({
        variables: { id, input },
      });
      return result.data?.updateFeed as FeedData;
    },
    [updateFeedMutation],
  );

  const updateStatus = useCallback(
    async (id: string, status: number) => {
      const result = await updateFeedStatusMutation({
        variables: { id, status },
      });
      return result.data?.updateFeedStatus as FeedData;
    },
    [updateFeedStatusMutation],
  );

  const togglePublic = useCallback(
    async (id: string, isPublic: boolean) => {
      const result = await makeFeedPublicMutation({
        variables: { id, isPublic },
      });
      return result.data?.makeFeedPublic as FeedData;
    },
    [makeFeedPublicMutation],
  );

  const deleteFeed = useCallback(
    async (id: string) => {
      await deleteFeedMutation({ variables: { id } });
    },
    [deleteFeedMutation],
  );

  const updateThumbnail = useCallback(
    async (id: string, thumbnail: string, bucket?: string) => {
      const result = await updateFeedThumbnailMutation({
        variables: { id, thumbnail, bucket },
      });
      return result.data?.updateFeedThumbnail as FeedData;
    },
    [updateFeedThumbnailMutation],
  );

  return {
    feed: feedData?.feedByPlaylistId ?? feedByIdData?.feedById ?? null,
    feedLoading: feedLoading || feedByIdLoading,
    categories: categoriesData?.feedsAdminCategories?.page?.edges?.map((e) => e.node) ?? [],
    loadFeed,
    loadFeedById,
    createFeed,
    updateFeed,
    updateStatus,
    togglePublic,
    deleteFeed,
    updateThumbnail,
  };
}
